<template>
    <div id="app">

        <!-- Record Detail Popup -->
        <div v-if="selectedRecord" class="simple-popup" @click.self="selectedRecord = null">
            <div class="popup-content wide-popup">
                <div class="popup-header">
                    <h3>Status Record — {{ selectedRecord.DeviceName || selectedRecord.DeviceId }}</h3>
                    <button @click="selectedRecord = null" class="close-btn">×</button>
                </div>
                <div class="popup-body">
                    <div class="detail-sections">

                        <!-- Identity -->
                        <div class="detail-section">
                            <h4><span class="source-icon">🔖</span> Identity</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Record ID:</strong> {{ selectedRecord.ID }}</div>
                                <div class="detail-row"><strong>Device ID (IMEI):</strong> {{ selectedRecord.DeviceId }}</div>
                                <div class="detail-row"><strong>Device Name:</strong> {{ selectedRecord.DeviceName || '—' }}</div>
                                <div class="detail-row"><strong>MAC Address:</strong> {{ selectedRecord.Macaddress || '—' }}</div>
                                <div class="detail-row"><strong>Event Time:</strong> {{ formatDateTime(selectedRecord.EventTime) }}</div>
                            </div>
                        </div>

                        <!-- Store -->
                        <div class="detail-section equipment-section">
                            <h4><span class="source-icon">🏪</span> Store</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Store ID:</strong> {{ selectedRecord.StoreID != null ? selectedRecord.StoreID : '—' }}</div>
                                <div class="detail-row"><strong>Store Name:</strong> {{ selectedRecord.StoreName || '—' }}</div>
                            </div>
                        </div>

                        <!-- GPS -->
                        <div class="detail-section cost-section">
                            <h4><span class="source-icon">📡</span> GPS</h4>
                            <div class="detail-grid">
                                <div class="detail-row"><strong>Lat / Lng:</strong> {{ selectedRecord.LatLng || '—' }}</div>
                                <div class="detail-row"><strong>Speed:</strong> {{ selectedRecord.Speed != null ? selectedRecord.Speed + ' km/h' : '—' }}</div>
                            </div>
                        </div>

                        <!-- Status -->
                        <div class="detail-section personnel-section">
                            <h4><span class="source-icon">⚡</span> Status</h4>
                            <div class="detail-grid">
                                <div class="detail-row">
                                    <strong>Priority:</strong>
                                    <span :class="['priority-badge', 'pri-' + String(selectedRecord.PriorityStatus || 'low').toLowerCase()]">
                                        {{ selectedRecord.PriorityStatus || '—' }}
                                    </span>
                                </div>
                                <div class="detail-row"><strong>Event Description:</strong> {{ selectedRecord.EventDescription || '—' }}</div>
                                <div class="detail-row">
                                    <strong>Power:</strong>
                                    <span :class="['power-badge', selectedRecord.PowerStatus === 'ON' ? 'pw-on' : 'pw-off']">
                                        {{ selectedRecord.PowerStatus || '—' }}
                                    </span>
                                </div>
                                <div class="detail-row">
                                    <strong>Ignition:</strong>
                                    <span :class="['ignition-badge', selectedRecord.IgnitionStatus === 'ON' ? 'ign-on' : 'ign-off']">
                                        {{ selectedRecord.IgnitionStatus || '—' }}
                                    </span>
                                </div>
                                <div class="detail-row">
                                    <strong>Override:</strong>
                                    <span :class="['override-badge', selectedRecord.OverrideStatus === 'ON' ? 'ov-on' : 'ov-off']">
                                        {{ selectedRecord.OverrideStatus || '—' }}
                                    </span>
                                </div>
                                <div class="detail-row">
                                    <strong>Battery:</strong>
                                    <span :class="{ 'battery-low': isBatteryLow(selectedRecord.Battery) }">
                                        {{ formatBattery(selectedRecord.Battery) }}
                                    </span>
                                </div>
                                <div class="detail-row">
                                    <strong>Battery Status:</strong>
                                    <span :class="['battery-status-badge', 'bs-' + String(selectedRecord.BatteryStatusID || 0)]">
                                        {{ selectedRecord.BatteryStatusMessage || '—' }}
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>

        <!-- Map Popup -->
        <div v-if="mapRecord" class="simple-popup" @click.self="mapRecord = null">
            <div class="popup-content map-popup">
                <div class="popup-header">
                    <h3>
                        📍 {{ mapRecord.DeviceName || mapRecord.DeviceId }}
                        <span style="font-weight:400;font-size:.85rem;margin-left:8px;opacity:.9;">{{ mapRecord.LatLng }}</span>
                    </h3>
                    <button @click="mapRecord = null" class="close-btn">×</button>
                </div>
                <div class="map-body">
                    <!-- Left: map -->
                    <div class="map-frame-wrap">
                        <iframe
                            v-if="mapIframeUrl"
                            :src="mapIframeUrl"
                            class="map-iframe"
                            frameborder="0"
                            scrolling="no"
                            allowfullscreen
                        ></iframe>
                        <div v-else class="map-no-coords">
                            <span>⚠️ No GPS coordinates available.</span>
                        </div>
                        <div class="map-footer">
                            <a v-if="mapGoogleUrl" :href="mapGoogleUrl" target="_blank" class="map-link-btn">
                                🗺️ Open in Google Maps
                            </a>
                            <span class="map-coords-label" v-if="mapRecord.LatLng">{{ mapRecord.LatLng }}</span>
                        </div>
                    </div>
                    <!-- Right: device info panel -->
                    <div class="map-info-panel">
                        <div class="mip-section">
                            <div class="mip-title">🔖 Identity</div>
                            <div class="mip-row"><span class="mip-l">Device ID</span><code class="mip-code">{{ mapRecord.DeviceId || '—' }}</code></div>
                            <div class="mip-row"><span class="mip-l">Device Name</span><span class="mip-v">{{ mapRecord.DeviceName || '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">MAC Address</span><span class="mip-v">{{ mapRecord.Macaddress || '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Event Time</span><span class="mip-v">{{ formatDateTime(mapRecord.EventTime) }}</span></div>
                        </div>
                        <div class="mip-section">
                            <div class="mip-title">🏪 Store</div>
                            <div class="mip-row"><span class="mip-l">Store</span><span class="mip-v">{{ mapRecord.StoreName || '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Store ID</span><span class="mip-v">{{ mapRecord.StoreID != null ? mapRecord.StoreID : '—' }}</span></div>
                        </div>
                        <div class="mip-section">
                            <div class="mip-title">📡 GPS</div>
                            <div class="mip-row"><span class="mip-l">Coordinates</span><code class="mip-coords">{{ mapRecord.LatLng || '—' }}</code></div>
                            <div class="mip-row"><span class="mip-l">Speed</span><span class="mip-v">{{ mapRecord.Speed != null ? mapRecord.Speed + ' km/h' : '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Altitude</span><span class="mip-v">{{ mapRecord.Altitude != null ? mapRecord.Altitude + ' m' : '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Angle</span><span class="mip-v">{{ mapRecord.Angle != null ? mapRecord.Angle + '°' : '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Satellites</span><span class="mip-v">{{ mapRecord.Sat != null ? mapRecord.Sat : '—' }}</span></div>
                        </div>
                        <div class="mip-section">
                            <div class="mip-title">⚡ Status</div>
                            <div class="mip-row"><span class="mip-l">Priority</span>
                                <span :class="['priority-badge', 'pri-' + String(mapRecord.PriorityStatus || 'low').toLowerCase()]">{{ mapRecord.PriorityStatus || '—' }}</span>
                            </div>
                            <div class="mip-row"><span class="mip-l">Power</span>
                                <span :class="['power-badge', mapRecord.PowerStatus === 'ON' ? 'pw-on' : 'pw-off']">{{ mapRecord.PowerStatus || '—' }}</span>
                            </div>
                            <div class="mip-row"><span class="mip-l">Ignition</span>
                                <span :class="['ignition-badge', mapRecord.IgnitionStatus === 'ON' ? 'ign-on' : 'ign-off']">{{ mapRecord.IgnitionStatus || '—' }}</span>
                            </div>
                            <div class="mip-row"><span class="mip-l">Override</span>
                                <span :class="['override-badge', mapRecord.OverrideStatus === 'ON' ? 'ov-on' : 'ov-off']">{{ mapRecord.OverrideStatus || '—' }}</span>
                            </div>
                            <div class="mip-row"><span class="mip-l">Event</span><span class="mip-v">{{ mapRecord.EventDescription || '—' }}</span></div>
                            <div class="mip-row"><span class="mip-l">Battery</span>
                                <span :class="{ 'battery-low': isBatteryLow(mapRecord.Battery) }">
                                    {{ formatBattery(mapRecord.Battery) }}
                                </span>
                            </div>
                            <div class="mip-row"><span class="mip-l">Battery Status</span>
                                <span :class="['battery-status-badge', 'bs-' + String(mapRecord.BatteryStatusID || 0)]">
                                    {{ mapRecord.BatteryStatusMessage || '—' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Stat Popup -->
        <div v-if="statPopupType" class="simple-popup" @click.self="statPopupType = null">
            <div class="popup-content stats-popup">
                <div class="popup-header">
                    <h3>{{ getStatPopupTitle() }}</h3>
                    <button @click="statPopupType = null" class="close-btn">×</button>
                </div>
                <div class="stats-popup-body">
                    <div class="stats-table-wrapper">
                        <table class="stats-data-table">
                                <thead>
                                    <tr>
                                        <th>Event Time</th>
                                        <th>Device ID</th>
                                        <th>Device Name</th>
                                        <th>Store</th>
                                        <th>Power</th>
                                        <th>Ignition</th>
                                        <th>Override</th>
                                        <th>Priority</th>
                                        <th>Battery (V)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="getStatPopupRecords().length === 0">
                                        <td colspan="9" class="empty-state" style="text-align: center; padding: 40px;">
                                            <div>No records found</div>
                                        </td>
                                    </tr>
                                    <tr v-for="(rec, idx) in getStatPopupRecords()" :key="idx" class="stats-table-row" @click="selectedRecord = rec">
                                        <td>{{ formatDateTime(rec.EventTime) }}</td>
                                        <td><code class="id-code">{{ rec.DeviceId || '—' }}</code></td>
                                        <td>{{ rec.DeviceName || '—' }}</td>
                                        <td>{{ rec.StoreName || (rec.StoreID != null ? '#' + rec.StoreID : '—') }}</td>
                                        <td>
                                            <span :class="['power-badge', rec.PowerStatus === 'ON' ? 'pw-on' : 'pw-off']">
                                                {{ rec.PowerStatus || '—' }}
                                            </span>
                                        </td>
                                        <td>
                                            <span :class="['ignition-badge', rec.IgnitionStatus === 'ON' ? 'ign-on' : 'ign-off']">
                                                {{ rec.IgnitionStatus || '—' }}
                                            </span>
                                        </td>
                                        <td>
                                            <span :class="['override-badge', rec.OverrideStatus === 'ON' ? 'ov-on' : 'ov-off']">
                                                {{ rec.OverrideStatus || '—' }}
                                            </span>
                                        </td>
                                        <td>
                                            <span :class="['priority-badge', 'pri-' + String(rec.PriorityStatus || 'low').toLowerCase()]">
                                                {{ rec.PriorityStatus || '—' }}
                                            </span>
                                        </td>
                                        <td :class="{ 'battery-low-cell': isBatteryLow(rec.Battery) }">
                                            <span :class="{ 'battery-low': isBatteryLow(rec.Battery) }">
                                                {{ formatBattery(rec.Battery) }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                </div>
            </div>
        </div>

        <!-- Header -->
        <div class="header-toolbar">
            <div class="header-content">
                <div class="logo-section">
                    <div class="logo-icon"><span>📡</span></div>
                    <h1 class="dashboard-title">IOT — FAMS Station override and power monitor</h1>
                </div>
                <div class="header-controls">
                    <div class="loader-controls">
                        <div class="loader-toggle">
                            <label class="toggle-label">
                                <input type="checkbox" v-model="autoRefreshEnabled" @change="handleAutoRefreshToggle" class="toggle-checkbox">
                                <span class="toggle-text">Auto Refresh</span>
                            </label>
                        </div>
                        <div v-if="autoRefreshEnabled" class="refresh-interval">
                            <label class="interval-label">
                                <span class="interval-text">Every:</span>
                                <select v-model="refreshInterval" @change="handleRefreshIntervalChange" class="interval-select">
                                    <option :value="30000">30 sec</option>
                                    <option :value="60000">1 min</option>
                                    <option :value="300000">5 min</option>
                                    <option :value="600000">10 min</option>
                                </select>
                            </label>
                        </div>
                        <button class="header-refresh-btn" @click="refreshData">
                            <span class="refresh-icon">🔄</span><span>Refresh</span>
                        </button>
                    </div>
                    <div class="live-indicator">
                        <div :class="['live-dot', { 'auto-refresh': autoRefreshEnabled }]"></div>
                        <span style="color:white;font-size:0.875rem;">
                            {{ autoRefreshEnabled ? 'Live (' + formatRefreshInterval() + ')' : 'Live' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Main Content -->
        <div class="main-content">

            <!-- Stats -->
            <div class="stats-section">
                <div class="stats-header">
                    <h3 class="stats-title">Device Overview <small style="font-size:.8rem;color:#888;">({{ selectedDateLabel }})</small></h3>
                    <button class="toggle-stats-btn" @click="statsVisible = !statsVisible">
                        <span class="toggle-icon">{{ statsVisible ? '📊' : '📈' }}</span>
                        {{ statsVisible ? 'Hide Statistics' : 'Show Statistics' }}
                    </button>
                </div>
                <transition name="slide-fade">
                    <div v-if="statsVisible" class="stats-wrapper">
                        <div class="stats-container">
                            <div class="stat-card clickable-stat total-stat" @click="openStatPopup('total')">
                                <div class="stat-label">Total Events</div>
                                <div class="stat-value">{{ summaryStats.totalEvents }}</div>
                                <div class="stat-change positive"><span class="stat-icon">📝</span><span>All records</span></div>
                                <div class="stat-hint">Click for details</div>
                            </div>
                            <div class="stat-card volume-stat">
                                <div class="stat-label">Active Devices</div>
                                <div class="stat-value" style="color:#4CAF50;">{{ summaryStats.uniqueDevices }}</div>
                                <div class="stat-change"><span class="stat-icon">📡</span><span>Unique IMEIs</span></div>
                            </div>
                            <div class="stat-card cost-stat">
                                <div class="stat-label">Ignition ON</div>
                                <div class="stat-value" style="color:#27AE60;">{{ summaryStats.ignitionOnCount }}</div>
                                <div class="stat-change positive"><span class="stat-icon">🔑</span><span>IgnitionStatus = ON</span></div>
                            </div>
                            <div class="stat-card equipment-stat">
                                <div class="stat-label">Moving Events</div>
                                <div class="stat-value" style="color:#2196F3;">{{ summaryStats.movingCount }}</div>
                                <div class="stat-change"><span class="stat-icon">🚗</span><span>Speed &gt; 0 km/h</span></div>
                            </div>
                            <div class="stat-card product-stat">
                                <div class="stat-label">Avg Speed</div>
                                <div class="stat-value" style="color:#9C27B0;">{{ formatNum(summaryStats.avgSpeed) }}</div>
                                <div class="stat-change"><span class="stat-icon">⚡</span><span>km/h across events</span></div>
                            </div>
                            <div class="stat-card average-stat clickable-stat" @click="openStatPopup('powerOn')">
                                <div class="stat-label">Power ON</div>
                                <div class="stat-value" style="color:#E67E22;">{{ summaryStats.powerOnCount }}</div>
                                <div class="stat-change"><span class="stat-icon">🔌</span><span>PowerStatus = ON</span></div>
                                <div class="stat-hint">Click for details</div>
                            </div>
                            <div class="stat-card alert-stat clickable-stat" @click="openStatPopup('powerOff')">
                                <div class="stat-label">Power OFF</div>
                                <div class="stat-value" style="color:#DC2626;">{{ summaryStats.powerOffCount }}</div>
                                <div class="stat-change"><span class="stat-icon">⚠️</span><span>PowerStatus = OFF</span></div>
                                <div class="stat-hint">Click for details</div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>

            <!-- Filters -->
            <div class="search-section" :class="{ collapsed: !filtersVisible }">
                <div class="filter-header">
                    <h3 class="filter-title">Filters</h3>
                    <div class="filter-header-buttons">
                        <!-- Active filter count badge -->
                        <span v-if="activeFilterCount > 0" class="active-filter-badge">{{ activeFilterCount }} active</span>
                        <button class="toggle-filter-btn" @click="filtersVisible = !filtersVisible">
                            {{ filtersVisible ? '▲ Hide' : '▼ Show' }}
                        </button>
                    </div>
                </div>
                <div v-if="filtersVisible" class="search-controls">

                    <!-- Row 1 -->
                    <!-- Date filter (single day — matches IOT-Activities FMS page) -->
                    <div class="filter-group date-filter-group">
                        <label class="filter-label">Date <span class="date-range-showing">{{ activeDateRangeLabel }}</span></label>
                        <input type="date" class="date-picker-input" v-model="selectedDate"
                            :max="getTodayDateString()" @change="handleDateChange" />
                    </div>

                    <!-- Device filter -->
                    <div class="filter-group">
                        <label class="filter-label">Device</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('devices')">
                                {{ filters.deviceIds.length === 0 ? 'All Devices' : filters.deviceIds.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.devices" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllDevices">
                                        {{ filters.deviceIds.length === uniqueDevices.length ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label v-for="d in uniqueDevices" :key="d.id" class="dropdown-item">
                                    <input type="checkbox" :value="d.id" v-model="filters.deviceIds" @change="applyFilters">
                                    {{ d.name }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Store filter -->
                    <div class="filter-group">
                        <label class="filter-label">Store</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('stores')">
                                {{ filters.storeIds.length === 0 ? 'All Stores' : filters.storeIds.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.stores" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllStores">
                                        {{ filters.storeIds.length === uniqueStores.length ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label v-for="s in uniqueStores" :key="s.id" class="dropdown-item">
                                    <input type="checkbox" :value="s.id" v-model="filters.storeIds" @change="applyFilters">
                                    {{ s.name }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Ignition filter -->
                    <div class="filter-group">
                        <label class="filter-label">Ignition</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('ignition')">
                                {{ filters.ignitionStatuses.length === 0 ? 'All' : filters.ignitionStatuses.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.ignition" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllIgnition">
                                        {{ filters.ignitionStatuses.length === 2 ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="ON" v-model="filters.ignitionStatuses" @change="applyFilters">
                                    ON
                                </label>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="OFF" v-model="filters.ignitionStatuses" @change="applyFilters">
                                    OFF
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Power filter -->
                    <div class="filter-group">
                        <label class="filter-label">Power</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('power')">
                                {{ filters.powerStatuses.length === 0 ? 'All' : filters.powerStatuses.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.power" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllPower">
                                        {{ filters.powerStatuses.length === 2 ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="ON" v-model="filters.powerStatuses" @change="applyFilters">
                                    ON
                                </label>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="OFF" v-model="filters.powerStatuses" @change="applyFilters">
                                    OFF
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Override filter -->
                    <div class="filter-group">
                        <label class="filter-label">Override</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('override')">
                                {{ filters.overrideStatuses.length === 0 ? 'All' : filters.overrideStatuses.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.override" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllOverride">
                                        {{ filters.overrideStatuses.length === 2 ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="ON" v-model="filters.overrideStatuses" @change="applyFilters">
                                    ON
                                </label>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="OFF" v-model="filters.overrideStatuses" @change="applyFilters">
                                    OFF
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Priority filter -->
                    <div class="filter-group">
                        <label class="filter-label">Priority</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('priority')">
                                {{ filters.priorityStatuses.length === 0 ? 'All' : filters.priorityStatuses.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.priority" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllPriority">
                                        {{ filters.priorityStatuses.length === 2 ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="High" v-model="filters.priorityStatuses" @change="applyFilters">
                                    High
                                </label>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="Low" v-model="filters.priorityStatuses" @change="applyFilters">
                                    Low
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Movement filter -->
                    <div class="filter-group">
                        <label class="filter-label">Movement</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('movement')">
                                {{ filters.movementTypes.length === 0 ? 'All' : filters.movementTypes.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.movement" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllMovement">
                                        {{ filters.movementTypes.length === 2 ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="moving" v-model="filters.movementTypes" @change="applyFilters">
                                    Moving (Speed &gt; 0)
                                </label>
                                <label class="dropdown-item">
                                    <input type="checkbox" value="stationary" v-model="filters.movementTypes" @change="applyFilters">
                                    Stationary (Speed = 0)
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Battery Status filter -->
                    <div class="filter-group">
                        <label class="filter-label">Battery Status</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('batteryStatus')">
                                {{ filters.batteryStatusIds.length === 0 ? 'All' : filters.batteryStatusIds.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.batteryStatus" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllBatteryStatus">
                                        {{ filters.batteryStatusIds.length === uniqueBatteryStatuses.length ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label v-for="bs in uniqueBatteryStatuses" :key="bs.id" class="dropdown-item">
                                    <input type="checkbox" :value="bs.id" v-model="filters.batteryStatusIds" @change="applyFilters">
                                    {{ bs.message }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Event Description multi-select -->
                    <div class="filter-group">
                        <label class="filter-label">Event Description</label>
                        <div class="multi-select-container">
                            <button class="multi-select-btn" @click="toggleDropdown('eventDescs')">
                                {{ filters.eventDescriptions.length === 0 ? 'All Events' : filters.eventDescriptions.length + ' selected' }}
                                <span class="dropdown-arrow">▼</span>
                            </button>
                            <div v-if="dropdownStates.eventDescs" class="multi-select-dropdown">
                                <div class="dropdown-search">
                                    <button class="select-all-btn" @click="toggleAllEventDescs">
                                        {{ filters.eventDescriptions.length === uniqueEventDescs.length ? 'Deselect All' : 'Select All' }}
                                    </button>
                                </div>
                                <label v-for="ev in uniqueEventDescs" :key="ev" class="dropdown-item">
                                    <input type="checkbox" :value="ev" v-model="filters.eventDescriptions" @change="applyFilters">
                                    {{ ev }}
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- Actions -->
                    <div class="filter-group filter-actions">
                        <button class="action-btn-filter load-btn" @click="loadData">
                            <span>🔍</span> Load Data
                        </button>
                        <button class="action-btn-filter reset-btn" @click="resetFilters">
                            <span>↺</span> Reset
                        </button>
                        <button class="action-btn-filter excel-btn" @click="exportData">
                            <span>📊</span> CSV
                        </button>
                        <button class="action-btn-filter print-btn" @click="popOutTable">
                            <span>🖨️</span> Pop-out
                        </button>
                    </div>

                </div>
            </div>

            <!-- Table -->
            <div class="grid-container">
                <div class="grid-header">
                    <div class="grid-title">
                        <span class="report-name">Status Events — {{ selectedDateLabel }}</span>
                        <span class="date-range-label">{{ filteredRecords.length }} records</span>
                    </div>
                    <div class="grid-controls">
                        <div v-if="loading" class="loading-indicator">
                            <div class="spinner"></div><span>Loading...</span>
                        </div>
                        <div class="rows-selector">
                            <label class="rows-label">Rows:</label>
                            <select v-model="itemsPerPage" @change="handleRowsChange" class="rows-select">
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                                <option :value="200">200</option>
                            </select>
                        </div>
                        <div class="grid-actions">
                            <button class="action-button export-btn" @click="exportData"><span>📊</span> CSV</button>
                            <button class="action-button popout-btn" @click="popOutTable"><span>🖨️</span> Pop-out</button>
                        </div>
                    </div>
                </div>

                <div class="table-container">
                    <table class="data-table">
                        <thead>
                            <tr>
                                <th>Event Time</th>
                                <th>Device ID</th>
                                <th>Device Name</th>
                                <th>Store</th>
                                <th>Speed</th>
                                <th>Priority</th>
                                <th>Event Desc</th>
                                <th>Power</th>
                                <th>Ignition</th>
                                <th>Override</th>
                                <th>Lat / Lng</th>
                                <th>Battery (V)</th>
                                <th>Battery Status</th>
                                <th>Map</th>
                            </tr>
                            <!-- Column search row -->
                            <tr class="search-row">
                                <th><input v-model="colSearch.EventTime" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.DeviceId" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.DeviceName" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.StoreName" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.Speed" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.PriorityStatus" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.EventDescription" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.PowerStatus" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.IgnitionStatus" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.OverrideStatus" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.LatLng" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.Battery" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                                <th><input v-model="colSearch.BatteryStatusMessage" @input="handleColumnSearch" class="col-search" placeholder="Search…"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="14" class="empty-state">
                                    <div class="loading-state">
                                        <div class="spinner large-spinner"></div>
                                        <span>Loading data…</span>
                                    </div>
                                </td>
                            </tr>
                            <tr v-else-if="paginatedRecords.length === 0">
                                <td colspan="14" class="empty-state">
                                    <div class="empty-icon">📡</div>
                                    <div class="empty-title">No Records Found</div>
                                    <div class="empty-description">No data for {{ selectedDateLabel }}</div>
                                </td>
                            </tr>
                            <tr v-for="(rec, i) in paginatedRecords" :key="rec.ID || i" @click="selectedRecord = rec"
                                :class="getRowClass(rec)" class="data-row">
                                <td>{{ formatDateTime(rec.EventTime) }}</td>
                                <td><code class="id-code">{{ rec.DeviceId || '—' }}</code></td>
                                <td>{{ rec.DeviceName || '—' }}</td>
                                <td>{{ rec.StoreName || (rec.StoreID != null ? '#' + rec.StoreID : '—') }}</td>
                                <td>{{ rec.Speed != null ? rec.Speed + ' km/h' : '—' }}</td>
                                <td>
                                    <span :class="['priority-badge', 'pri-' + String(rec.PriorityStatus || 'low').toLowerCase()]">
                                        {{ rec.PriorityStatus || '—' }}
                                    </span>
                                </td>
                                <td class="desc-cell">{{ rec.EventDescription || '—' }}</td>
                                <td>
                                    <span :class="['power-badge', rec.PowerStatus === 'ON' ? 'pw-on' : 'pw-off']">
                                        {{ rec.PowerStatus || '—' }}
                                    </span>
                                </td>
                                <td>
                                    <span :class="['ignition-badge', rec.IgnitionStatus === 'ON' ? 'ign-on' : 'ign-off']">
                                        {{ rec.IgnitionStatus || '—' }}
                                    </span>
                                </td>
                                <td>
                                    <span :class="['override-badge', rec.OverrideStatus === 'ON' ? 'ov-on' : 'ov-off']">
                                        {{ rec.OverrideStatus || '—' }}
                                    </span>
                                </td>
                                <td class="latlng-cell">{{ rec.LatLng || '—' }}</td>
                                <td :class="{ 'battery-low-cell': isBatteryLow(rec.Battery) }">
                                    <span :class="{ 'battery-low': isBatteryLow(rec.Battery) }">
                                        {{ formatBattery(rec.Battery) }}
                                    </span>
                                </td>
                                <td class="status-message-cell">
                                    <span :class="['battery-status-badge', 'bs-' + String(rec.BatteryStatusID || 0)]">
                                        {{ rec.BatteryStatusMessage || '—' }}
                                    </span>
                                </td>
                                <td class="map-cell" @click.stop>
                                    <div v-if="rec.LatLng" class="map-btn-wrap">
                                        <button
                                            class="map-pin-btn"
                                            @click.stop="openMap(rec)"
                                        >📍 Map</button>
                                    </div>
                                    <span v-else class="no-gps">—</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div><!-- /table-container -->

                <!-- Pagination -->
                <div v-if="totalPages > 1" class="pagination-container">
                    <div class="pagination-info">
                        Showing {{ (currentPage - 1) * itemsPerPage + 1 }}–{{ Math.min(currentPage * itemsPerPage, filteredRecords.length) }}
                        of {{ filteredRecords.length }} entries
                    </div>
                    <div class="pagination-controls">
                        <button class="page-btn prev-btn" @click="currentPage = 1" :disabled="currentPage === 1">« First</button>
                        <button class="page-btn prev-btn" @click="currentPage--" :disabled="currentPage === 1">‹ Prev</button>
                        <button v-for="p in visiblePageNumbers" :key="p" class="page-btn page-number"
                            :class="{ active: p === currentPage }" @click="currentPage = p">{{ p }}</button>
                        <button class="page-btn next-btn" @click="currentPage++" :disabled="currentPage === totalPages">Next ›</button>
                        <button class="page-btn next-btn" @click="currentPage = totalPages" :disabled="currentPage === totalPages">Last »</button>
                    </div>
                </div>

            </div><!-- /grid-container -->
        </div>
    </div>
</template>

<script>
export default {
    name: 'IOTStatusPO',

    data: function () {
        return {
            autoRefreshEnabled: false,
            refreshInterval: 60000,
            refreshTimer: null,
            _handleClickOutsideBound: null,
            loading: false,

            currentPage: 1,
            itemsPerPage: 25,
            records: [],
            batteryLowThreshold: 3500,
            statPopupType: null,

            // Selected day (YYYY-MM-DD) — data is fetched server-side for this day only. Defaults to today.
            selectedDate: (function () {
                var d = new Date();
                return d.getFullYear() + '-' +
                    String(d.getMonth() + 1).padStart(2, '0') + '-' +
                    String(d.getDate()).padStart(2, '0');
            })(),

            summaryStats: {
                totalEvents: 0,
                uniqueDevices: 0,
                ignitionOnCount: 0,
                movingCount: 0,
                avgSpeed: 0,
                powerOnCount: 0,
                powerOffCount: 0
            },

            filters: {
                deviceIds: [],
                storeIds: [],
                ignitionStatuses: [],
                powerStatuses: [],
                overrideStatuses: [],
                priorityStatuses: [],
                movementTypes: [],
                batteryStatusIds: [],
                eventDescriptions: []
            },

            colSearch: {
                EventTime: '',
                DeviceId: '',
                DeviceName: '',
                StoreName: '',
                EventDescription: '',
                LatLng: '',
                Speed: '',
                PriorityStatus: '',
                PowerStatus: '',
                IgnitionStatus: '',
                OverrideStatus: '',
                Battery: '',
                BatteryStatusMessage: ''
            },

            filtersVisible: true,
            statsVisible: true,

            dropdownStates: { 
                devices: false, 
                stores: false, 
                ignition: false,
                power: false,
                override: false,
                priority: false,
                movement: false,
                batteryStatus: false,
                eventDescs: false 
            },

            uniqueDevices: [],
            uniqueStores: [],
            uniqueBatteryStatuses: [],
            uniqueEventDescs: [],

            selectedRecord: null,
            mapRecord: null,
            leafletMap: null
        };
    },

    computed: {
        // Filter-panel label showing the active day, matching the IOT-Activities FMS page.
        activeDateRangeLabel: function () {
            if (this.selectedDate) return 'Showing: ' + this.selectedDate + ' (00:00 – 23:59)';
            return '';
        },

        // Human-friendly label for the selected day, e.g. "30 Jul 2026".
        selectedDateLabel: function () {
            if (!this.selectedDate) return '';
            var parts = String(this.selectedDate).split('-');
            if (parts.length < 3) return this.selectedDate;
            var d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
            var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return d.getDate() + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
        },

        activeFilterCount: function () {
            var f = this.filters;
            var count = 0;
            if (f.deviceIds.length)          count++; 
            if (f.storeIds.length)           count++; 
            if (f.ignitionStatuses.length)   count++; 
            if (f.powerStatuses.length)      count++; 
            if (f.overrideStatuses.length)   count++; 
            if (f.priorityStatuses.length)   count++; 
            if (f.movementTypes.length)      count++; 
            if (f.batteryStatusIds.length)   count++; 
            if (f.eventDescriptions.length)  count++;
            return count;
        },

        filteredRecords: function () {
            var self = this;
            var filtered = this.records.slice();

            if (this.filters.deviceIds && this.filters.deviceIds.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.deviceIds.indexOf(r.DeviceId) !== -1; });
            }
            if (this.filters.storeIds && this.filters.storeIds.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.storeIds.indexOf(r.StoreID) !== -1; });
            }
            if (this.filters.ignitionStatuses && this.filters.ignitionStatuses.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.ignitionStatuses.indexOf(r.IgnitionStatus) !== -1; });
            }
            if (this.filters.powerStatuses && this.filters.powerStatuses.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.powerStatuses.indexOf(r.PowerStatus) !== -1; });
            }
            if (this.filters.overrideStatuses && this.filters.overrideStatuses.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.overrideStatuses.indexOf(r.OverrideStatus) !== -1; });
            }
            if (this.filters.priorityStatuses && this.filters.priorityStatuses.length > 0) {
                filtered = filtered.filter(function (r) { return self.filters.priorityStatuses.indexOf(r.PriorityStatus) !== -1; });
            }

            if (this.filters.batteryStatusIds && this.filters.batteryStatusIds.length > 0) {
                filtered = filtered.filter(function (r) { 
                    return self.filters.batteryStatusIds.indexOf(r.BatteryStatusID) !== -1; 
                });
            }

            if (this.filters.movementTypes && this.filters.movementTypes.length > 0) {
                filtered = filtered.filter(function (r) {
                    var isMoving = r.Speed != null && parseFloat(r.Speed) > 0;
                    return (self.filters.movementTypes.indexOf('moving') !== -1 && isMoving) ||
                           (self.filters.movementTypes.indexOf('stationary') !== -1 && !isMoving);
                });
            }

            if (this.filters.eventDescriptions && this.filters.eventDescriptions.length > 0) {
                filtered = filtered.filter(function (r) {
                    return self.filters.eventDescriptions.indexOf(r.EventDescription) !== -1;
                });
            }

            var cs = this.colSearch;
                filtered = filtered.filter(function (r) {
                    if (cs.EventTime && !self.formatDateTime(r.EventTime).toLowerCase().includes(cs.EventTime.toLowerCase())) return false;
                    if (cs.DeviceId && !(r.DeviceId || '').toLowerCase().includes(cs.DeviceId.toLowerCase())) return false;
                    if (cs.DeviceName && !(r.DeviceName || '').toLowerCase().includes(cs.DeviceName.toLowerCase())) return false;
                    if (cs.StoreName && !(r.StoreName || '').toLowerCase().includes(cs.StoreName.toLowerCase())) return false;
                    if (cs.EventDescription && !(r.EventDescription || '').toLowerCase().includes(cs.EventDescription.toLowerCase())) return false;
                    if (cs.LatLng && !(r.LatLng || '').toLowerCase().includes(cs.LatLng.toLowerCase())) return false;
                    
                    // 🆕 ADD THESE 7 NEW FILTERS:
                    if (cs.Speed) {
                        var speedStr = r.Speed != null ? String(r.Speed) : '';
                        if (!speedStr.toLowerCase().includes(cs.Speed.toLowerCase())) return false;
                    }
                    if (cs.PriorityStatus && !(r.PriorityStatus || '').toLowerCase().includes(cs.PriorityStatus.toLowerCase())) return false;
                    if (cs.PowerStatus && !(r.PowerStatus || '').toLowerCase().includes(cs.PowerStatus.toLowerCase())) return false;
                    if (cs.IgnitionStatus && !(r.IgnitionStatus || '').toLowerCase().includes(cs.IgnitionStatus.toLowerCase())) return false;
                    if (cs.OverrideStatus && !(r.OverrideStatus || '').toLowerCase().includes(cs.OverrideStatus.toLowerCase())) return false;
                    if (cs.Battery) {
                        var batteryStr = self.formatBattery(r.Battery).toLowerCase();
                        if (!batteryStr.includes(cs.Battery.toLowerCase())) return false;
                    }
                    if (cs.BatteryStatusMessage && !(r.BatteryStatusMessage || '').toLowerCase().includes(cs.BatteryStatusMessage.toLowerCase())) return false;
                    
                    return true;
                });

            return filtered;
        },

        totalPages: function () { return Math.ceil(this.filteredRecords.length / this.itemsPerPage); },

        paginatedRecords: function () {
            var start = (this.currentPage - 1) * this.itemsPerPage;
            return this.filteredRecords.slice(start, start + this.itemsPerPage);
        },

        visiblePageNumbers: function () {
            var maxV = 5, pages = [];
            var start = Math.max(1, this.currentPage - 2);
            var end = Math.min(this.totalPages, start + maxV - 1);
            if (end - start < maxV - 1) start = Math.max(1, end - maxV + 1);
            for (var i = start; i <= end; i++) pages.push(i);
            return pages;
        },

        mapCoords: function () {
            if (!this.mapRecord || !this.mapRecord.LatLng) return null;
            var parts = String(this.mapRecord.LatLng).split(',');
            if (parts.length < 2) return null;
            var lat = parseFloat(parts[0].trim());
            var lng = parseFloat(parts[1].trim());
            if (isNaN(lat) || isNaN(lng)) return null;
            return { lat: lat, lng: lng };
        },

        mapIframeUrl: function () {
            var c = this.mapCoords;
            if (!c) return '';
            var delta = 0.01;
            var bbox = [c.lng - delta, c.lat - delta, c.lng + delta, c.lat + delta].join(',');
            return 'https://www.openstreetmap.org/export/embed.html?bbox=' + bbox +
                   '&layer=mapnik&marker=' + c.lat + ',' + c.lng;
        },

        mapGoogleUrl: function () {
            var c = this.mapCoords;
            if (!c) return '';

            var label = '';
            if (this.mapRecord) {
                var name  = this.mapRecord.DeviceName  || this.mapRecord.DeviceId || '';
                var store = this.mapRecord.StoreName   || '';
                var ign   = this.mapRecord.IgnitionStatus || '';
                label = [name, store, ign].filter(Boolean).join(' | ');
            }

            var query = encodeURIComponent(c.lat + ',' + c.lng);
            var title = encodeURIComponent(label);

            return `https://www.google.com/maps/search/?api=1&query=${query}&query_place_id=${title}`;
        }
    },

    methods: {

        formatBattery: function (batteryMV) {
            if (!batteryMV) return '—';
            var voltageV = parseFloat(batteryMV) / 1000;
            return voltageV.toFixed(2) + ' V';
        },

        isBatteryLow: function (batteryMV) {
            if (!batteryMV) return false;
            var batteryVal = parseFloat(batteryMV);
            return batteryVal <= this.batteryLowThreshold;
        },

        openMap: function (rec) {
            this.mapRecord = rec;
        },

        closeMap: function () {
            this.mapRecord = null;
        },

        openStatPopup: function (type) {
            this.statPopupType = type;
        },

        getStatPopupTitle: function () {
            if (this.statPopupType === 'total') return '📝 All Events';
            if (this.statPopupType === 'powerOn') return '🔌 Power ON Events';
            if (this.statPopupType === 'powerOff') return '⚠️ Power OFF Events';
            return 'Events';
        },

        getStatPopupRecords: function () {
            var self = this;
            if (this.statPopupType === 'total') {
                return this.records;
            }
            if (this.statPopupType === 'powerOn') {
                return this.records.filter(function (r) { return r.PowerStatus === 'ON'; });
            }
            if (this.statPopupType === 'powerOff') {
                return this.records.filter(function (r) { return r.PowerStatus === 'OFF'; });
            }
            return [];
        },

        formatDateTime: function (date) {
            if (!date) return 'N/A';
            var d = new Date(date);
            return d.getFullYear() + '/' +
                String(d.getMonth() + 1).padStart(2, '0') + '/' +
                String(d.getDate()).padStart(2, '0') + ' ' +
                String(d.getHours()).padStart(2, '0') + ':' +
                String(d.getMinutes()).padStart(2, '0');
        },

        formatNum: function (v) { return v != null ? parseFloat(v).toFixed(1) : '0.0'; },

        getRowClass: function (rec) {
            if (rec.IgnitionStatus === 'ON' && rec.Speed > 0) return 'row-moving';
            if (rec.IgnitionStatus === 'ON') return 'row-idle';
            if (rec.PowerStatus === 'OFF') return 'row-failed';
            return '';
        },

        loadData: function () {
            var self = this;
            self.loading = true;

            var url = 'IOTStatusFMC125?date=' + encodeURIComponent(self.selectedDate || '');

            var safetyTimer = setTimeout(function () {
                if (self.loading) {
                    self.loading = false;
                    self.showNotification('Warning', 'Request timed out — check API connectivity', 'warning');
                }
            }, 15000);

            var onSuccess = function (response) {
            debugger;
                var data = (response && response.data !== undefined) ? response.data : response;

                if (data && !Array.isArray(data) && (data.Error === 1 || data.error === 1)) {
                    self.showNotification('Error', data.Message || data.message || 'API error', 'danger');
                    self.records = [];
                    return;
                }

                if (Array.isArray(data)) {
                    self.records = data.map(function (item) {
                        var di1   = item.digitalInput1   != null ? parseInt(item.digitalInput1)   : 0;
                        var di2   = item.digitalInput2   != null ? parseInt(item.digitalInput2)   : 0;
                        var di239 = item.digitalInput239 != null ? parseInt(item.digitalInput239) : 0;

                        var powerStatus    = di1   === 1 ? 'ON' : 'OFF';
                        var ignitionStatus = di239 === 1 ? 'ON' : 'OFF';
                        var overrideStatus = (di2 === 0 && powerStatus === 'ON') ? 'ON' : 'OFF';

                        var deviceId = item.deviceId || '';
                        var storeID  = item.storeID != null ? parseInt(item.storeID, 10) : null;

                        var priorityStatus = (powerStatus === 'OFF' || overrideStatus === 'ON') ? 'High' : 'Low';

                        return {
                            ID:               item.id,
                            DeviceId:         deviceId,
                            DeviceName:       item.deviceName   || deviceId,
                            Macaddress:       item.macaddress   || deviceId,
                            Account:          item.account      || '—',
                            AccountID:        item.accountID,
                            EventTime:        item.eventTime,
                            CreateDate:       item.createDate,
                            EnqueueTime:      item.enqueueTime,
                            LatLng:           item.latLng       || '',
                            Speed:            item.speed,
                            Altitude:         item.altitude,
                            Angle:            item.angle,
                            Sat:              item.sat,
                            Battery:          item.batteryMV,
                            BatteryStatusID:  item.batteryStatusID,
                            BatteryStatusMessage: item.batteryStatusMessage || '',
                            PowerStatus:      powerStatus,
                            IgnitionStatus:   ignitionStatus,    
                            OverrideStatus:   overrideStatus,
                            PriorityStatus:   priorityStatus,
                            EventDescription: item.eventDescription || (item.event != null ? String(item.event) : '—'),
                            StoreID:          storeID,
                            StoreName:        item.storeName    || (storeID != null ? 'Store #' + storeID : 'N/A'),
                            IotDeviceID:      item.iotDeviceID,
                            IotName:          item.iotName      || '—',
                            IotType:          item.iotType      || '—',
                            IotTypeDesc:      item.iotTypeDesc  || '—',
                            IotBrand:         item.iotBrand     || '—',
                            DigitalInput1:    di1,
                            DigitalInput2:    di2,
                            DigitalInput239:  di239,
                            Sid:              item.sid,
                            Timestamp:        item.timestamp
                        };
                    });
                    self.calculateSummaryStats();
                    self.extractUniqueValues();
                    self.showNotification('Success', 'Loaded ' + self.records.length + ' records', 'success');
                } else {
                    self.records = [];
                    self.showNotification('Warning', 'No data returned', 'warning');
                }
            };

            var onFinally = function () {
                clearTimeout(safetyTimer);
                self.loading = false;
            };

            try {
                self.$ajaxGet(self, url, onSuccess, onFinally);
            } catch (e) {
                clearTimeout(safetyTimer);
                self.loading = false;
                self.showNotification('Error', 'Failed to reach API: ' + e.message, 'danger');
                console.error('loadData error:', e);
            }
        },

        calculateSummaryStats: function () {
            var devices = new Set(), ignOn = 0, moving = 0, powerOn = 0, powerOff = 0, totalSpeed = 0, speedCount = 0;
            this.records.forEach(function (r) {
                if (r.DeviceId) devices.add(r.DeviceId);
                if (r.IgnitionStatus === 'ON') ignOn++;
                if (r.PowerStatus === 'ON') powerOn++;
                if (r.PowerStatus === 'OFF') powerOff++;
                if (r.Speed != null && parseFloat(r.Speed) > 0) {
                    moving++;
                    totalSpeed += parseFloat(r.Speed);
                    speedCount++;
                }
            });
            this.summaryStats = {
                totalEvents:     this.records.length,
                uniqueDevices:   devices.size,
                ignitionOnCount: ignOn,
                movingCount:     moving,
                avgSpeed:        speedCount > 0 ? totalSpeed / speedCount : 0,
                powerOnCount:    powerOn,
                powerOffCount:   powerOff
            };
        },

        extractUniqueValues: function () {
            var devMap = {}, storeMap = {}, evSet = {}, bsMap = {};
            this.records.forEach(function (r) {
                if (r.DeviceId) devMap[r.DeviceId] = r.DeviceName || r.DeviceId;
                if (r.StoreID != null) storeMap[r.StoreID] = r.StoreName || ('Store ' + r.StoreID);
                if (r.EventDescription && r.EventDescription !== '—') evSet[r.EventDescription] = true;
                if (r.BatteryStatusID != null) bsMap[r.BatteryStatusID] = r.BatteryStatusMessage || 'Unknown';
            });
            this.uniqueDevices = Object.keys(devMap).map(function (k) {
                return { id: k, name: devMap[k] };
            }).sort(function (a, b) { return a.name.localeCompare(b.name); });
            this.uniqueStores = Object.keys(storeMap).map(function (k) {
                return { id: parseInt(k), name: storeMap[k] };
            }).sort(function (a, b) { return a.name.localeCompare(b.name); });
            this.uniqueBatteryStatuses = Object.keys(bsMap).map(function (k) {
                return { id: parseInt(k), message: bsMap[k] };
            }).sort(function (a, b) { return a.id - b.id; });
            this.uniqueEventDescs = Object.keys(evSet).sort();
        },

        applyFilters: function () { this.currentPage = 1; },

        // Today as YYYY-MM-DD — caps the date picker so future days can't be chosen.
        getTodayDateString: function () {
            var d = new Date();
            return d.getFullYear() + '-' +
                String(d.getMonth() + 1).padStart(2, '0') + '-' +
                String(d.getDate()).padStart(2, '0');
        },

        // Reload server-side data for the newly selected day.
        handleDateChange: function () {
            if (!this.selectedDate) {
                this.selectedDate = this.getTodayDateString();
            }
            this.currentPage = 1;
            this.loadData();
        },

        resetFilters: function () {
            this.filters = {
                deviceIds: [], storeIds: [],
                ignitionStatuses: [], powerStatuses: [], overrideStatuses: [],
                priorityStatuses: [], movementTypes: [], batteryStatusIds: [], eventDescriptions: []
            };
            this.colSearch = {
                EventTime: '', DeviceId: '', DeviceName: '', StoreName: '',
                Speed: '', PriorityStatus: '', EventDescription: '',
                PowerStatus: '', IgnitionStatus: '', OverrideStatus: '',
                LatLng: '', Battery: '', BatteryStatusMessage: ''
            };
            this.currentPage = 1;
            this.loadData();
            this.showNotification('Success', 'Filters reset', 'success');
        },

        clearAllFilters: function () {
            this.filters.deviceIds          = [];
            this.filters.storeIds           = [];
            this.filters.ignitionStatuses   = [];
            this.filters.powerStatuses      = [];
            this.filters.overrideStatuses   = [];
            this.filters.priorityStatuses   = [];
            this.filters.movementTypes      = [];
            this.filters.batteryStatusIds   = [];
            this.filters.eventDescriptions  = [];
            this.currentPage = 1;
        },

        handleColumnSearch: function () { this.currentPage = 1; },
        handleRowsChange: function () { this.currentPage = 1; },
        refreshData: function () { this.showNotification('Info', 'Refreshing...', 'primary'); this.loadData(); },

        toggleDropdown: function (type) {
            var self = this;
            Object.keys(this.dropdownStates).forEach(function (k) { if (k !== type) self.dropdownStates[k] = false; });
            this.dropdownStates[type] = !this.dropdownStates[type];
        },

        toggleAllDevices: function () {
            this.filters.deviceIds = this.filters.deviceIds.length === this.uniqueDevices.length
                ? [] : this.uniqueDevices.map(function (d) { return d.id; });
            this.applyFilters();
        },

        toggleAllStores: function () {
            this.filters.storeIds = this.filters.storeIds.length === this.uniqueStores.length
                ? [] : this.uniqueStores.map(function (s) { return s.id; });
            this.applyFilters();
        },

        toggleAllIgnition: function () {
            this.filters.ignitionStatuses = this.filters.ignitionStatuses.length === 2 ? [] : ['ON', 'OFF'];
            this.applyFilters();
        },

        toggleAllPower: function () {
            this.filters.powerStatuses = this.filters.powerStatuses.length === 2 ? [] : ['ON', 'OFF'];
            this.applyFilters();
        },

        toggleAllOverride: function () {
            this.filters.overrideStatuses = this.filters.overrideStatuses.length === 2 ? [] : ['ON', 'OFF'];
            this.applyFilters();
        },

        toggleAllPriority: function () {
            this.filters.priorityStatuses = this.filters.priorityStatuses.length === 2 ? [] : ['High', 'Low'];
            this.applyFilters();
        },

        toggleAllMovement: function () {
            this.filters.movementTypes = this.filters.movementTypes.length === 2 ? [] : ['moving', 'stationary'];
            this.applyFilters();
        },

        toggleAllBatteryStatus: function () {
            this.filters.batteryStatusIds = this.filters.batteryStatusIds.length === this.uniqueBatteryStatuses.length
                ? [] : this.uniqueBatteryStatuses.map(function (bs) { return bs.id; });
            this.applyFilters();
        },

        toggleAllEventDescs: function () {
            this.filters.eventDescriptions = this.filters.eventDescriptions.length === this.uniqueEventDescs.length
                ? [] : this.uniqueEventDescs.slice();
            this.applyFilters();
        },

        exportData: function () {
            var self = this;
            var headers = 'Event Time,Device ID,Device Name,Store,Speed,Priority,Event Desc,Power,Ignition,Override,Lat-Lng,Battery,Battery Status';
            var rows = this.filteredRecords.map(function (r) {
                return [
                    self.formatDateTime(r.EventTime),
                    r.DeviceId || '',
                    r.DeviceName || '',
                    r.StoreName || '',
                    r.Speed != null ? r.Speed : '',
                    r.PriorityStatus || '',
                    r.EventDescription || '',
                    r.PowerStatus || '',
                    r.IgnitionStatus || '',
                    r.OverrideStatus || '',
                    r.LatLng || '',
                    self.formatBattery(r.Battery),
                    r.BatteryStatusMessage || ''
                ].map(function (v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
            }).join('\n');
            var a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([[headers, rows].join('\n')], { type: 'text/csv' }));
            a.download = 'iot_fmc125_' + Date.now() + '.csv';
            a.click();
            this.showNotification('Success', 'Data exported', 'success');
        },

        popOutTable: function () {
            var self = this;
            var w = window.open('', '_blank', 'width=1400,height=800,scrollbars=yes,resizable=yes');
            if (!w) { this.showNotification('Error', 'Allow popups for this site', 'danger'); return; }
            var cols = ['Event Time', 'Device ID', 'Device Name', 'Store', 'Speed', 'Priority', 'Event Desc', 'Power', 'Ignition', 'Override', 'Lat/Lng', 'Battery (V)', 'Battery Status'];
            var html = '<!DOCTYPE html><html><head><title>Status</title>' +
                '<style>body{font-family:Arial,sans-serif;margin:20px}h1{color:#D2691E;font-size:1.4rem}' +
                'table{width:100%;border-collapse:collapse}th{background:#D2691E;color:#fff;padding:10px;text-align:left;position:sticky;top:0}' +
                'td{padding:8px 10px;border-bottom:1px solid #e0e0e0;font-size:12px}tr:hover{background:#f5f5f5}' +
                '.low-battery{background-color:#ffcccc;font-weight:bold;}.no-risk{background-color:#ccffcc;}.low-risk{background-color:#ffffcc;}</style>' +
                '</head><body><h1>Status Events — ' + this.selectedDateLabel + '</h1><p>' + this.filteredRecords.length + ' records</p>' +
                '<table><thead><tr>' + cols.map(function (c) { return '<th>' + c + '</th>'; }).join('') + '</tr></thead><tbody>';
            this.filteredRecords.forEach(function (r) {
                var batteryClass = self.isBatteryLow(r.Battery) ? ' class="low-battery"' : '';
                var statusClass = r.BatteryStatusID === 0 ? ' class="no-risk"' : (r.BatteryStatusID === 1 ? ' class="low-risk"' : '');
                html += '<tr><td>' + self.formatDateTime(r.EventTime) + '</td>' +
                    '<td>' + (r.DeviceId || '—') + '</td>' +
                    '<td>' + (r.DeviceName || '—') + '</td>' +
                    '<td>' + (r.StoreName || (r.StoreID != null ? '#' + r.StoreID : '—')) + '</td>' +
                    '<td>' + (r.Speed != null ? r.Speed + ' km/h' : '—') + '</td>' +
                    '<td>' + (r.PriorityStatus || '—') + '</td>' +
                    '<td>' + (r.EventDescription || '—') + '</td>' +
                    '<td>' + (r.PowerStatus || '—') + '</td>' +
                    '<td>' + (r.IgnitionStatus || '—') + '</td>' +
                    '<td>' + (r.OverrideStatus || '—') + '</td>' +
                    '<td>' + (r.LatLng || '—') + '</td>' +
                    '<td' + batteryClass + '>' + self.formatBattery(r.Battery) + '</td>' +
                    '<td' + statusClass + '>' + (r.BatteryStatusMessage || '—') + '</td></tr>';
            });
            html += '</tbody></table></body></html>';
            w.document.write(html); w.document.close();
            this.showNotification('Success', 'Opened in new window', 'success');
        },

        handleAutoRefreshToggle: function () {
            if (this.autoRefreshEnabled) { this.startAutoRefresh(); this.showNotification('Success', 'Auto-refresh enabled', 'success'); }
            else { this.stopAutoRefresh(); this.showNotification('Info', 'Auto-refresh disabled', 'primary'); }
        },
        handleRefreshIntervalChange: function () { if (this.autoRefreshEnabled) { this.stopAutoRefresh(); this.startAutoRefresh(); } },
        startAutoRefresh: function () {
            var self = this; this.stopAutoRefresh();
            if (this.autoRefreshEnabled) this.refreshTimer = setInterval(function () { self.loadData(); }, this.refreshInterval);
        },
        stopAutoRefresh: function () { if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; } },
        formatRefreshInterval: function () { var s = this.refreshInterval / 1000; return s < 60 ? s + 's' : (s / 60) + 'm'; },

        showNotification: function (title, text, type) {
            if (this.$vs && this.$vs.notify) this.$vs.notify({ title: title, text: text, color: type, position: 'top-right' });
            else console.log('[' + title + '] ' + text);
        },

        handleClickOutside: function (event) {
            if (!this.$el) return;
            var inside = false, self = this;
            this.$el.querySelectorAll('.multi-select-container').forEach(function (c) { if (c.contains(event.target)) inside = true; });
            if (!inside) Object.keys(this.dropdownStates).forEach(function (k) { self.dropdownStates[k] = false; });
        }
    },

    mounted: function () {
        var self = this;
        // Deep-link from FMS Status: /IOTStatusPO?storeID=123 pre-filters to
        // that station's records. filteredRecords reacts to filters.storeIds
        // once loadData() populates this.records, so this can be set first.
        if (this.$route.query.storeID) {
            var storeID = parseInt(this.$route.query.storeID, 10);
            if (!isNaN(storeID)) this.filters.storeIds = [storeID];
        }
        this.loadData();
        this._handleClickOutsideBound = function (event) { self.handleClickOutside(event); };
        document.addEventListener('click', this._handleClickOutsideBound);
        this.showNotification('Welcome!', 'Status Monitor — Ready', 'success');
    },

    beforeDestroy: function () {
        this.stopAutoRefresh();
        if (this._handleClickOutsideBound) document.removeEventListener('click', this._handleClickOutsideBound);
    }
};
</script>

<style>
:root {
    --autumn-primary: #D2691E;
    --autumn-accent: #FF8C00;
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
}

body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #e2e8f0;
    height: 100vh;
    overflow: hidden;
    width: 100%;
}

#app {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    max-width: none;
}

/* ─── Battery Status Styling ──────────────────────────────────────────────── */
.battery-status-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .3px;
}

.bs-0 { 
    background: rgba(76, 175, 80, .15);
    color: #065f46; 
}

.bs-1 { 
    background: rgba(255, 193, 7, .2);
    color: #a16207; 
}

.status-message-cell {
    text-align: center;
}

/* ─── Battery Low Styling ─────────────────────────────────────────────────── */
.battery-low {
    background-color: #ff4444 !important;
    color: white !important;
    font-weight: bold;
    padding: 4px 8px;
    border-radius: 4px;
    display: inline-block;
}

.battery-low-cell {
    background-color: #ffcccc;
}

/* ─── Stat Popup Styling ──────────────────────────────────────────────────── */
.stats-popup {
    width: 1100px;
    max-width: 95vw;
    max-height: 65vh;
    display: flex;
    flex-direction: column;
}

.stats-popup-body {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.stats-table-wrapper {
    flex: 1;
    overflow: auto;
    min-height: 0;
}

.stats-data-table {
    width: 100%;
    border-collapse: collapse;
}

.stats-data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.stats-data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.stats-data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s;
    background: #fff;
    cursor: pointer;
}

.stats-data-table tbody tr:hover {
    background: #f1f5f9;
}

.stats-table-row {
    transition: all .2s;
}

.stats-table-row:hover {
    background: #fff8f3 !important;
}

.stats-data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
}

/* ─── Header ──────────────────────────────────────────────────────────────── */
.header-toolbar {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    padding: 16px 0;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 20px;
}

.logo-section {
    display: flex;
    align-items: center;
    gap: 12px;
}

.logo-icon {
    width: 40px;
    height: 40px;
    background: white;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: var(--autumn-primary);
}

.dashboard-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 600;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

/* Date filter — matches the IOT-Activities (FMS) page */
.date-filter-group {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    padding: 8px 6px;
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
}

.date-range-showing {
    font-size: 0.75rem;
    font-weight: normal;
    color: #64748b;
    text-transform: none;
    letter-spacing: 0;
    display: inline-block;
    margin-left: 6px;
}

.date-picker-input {
    padding: 6px 10px;
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    font-size: 0.85rem;
    color: #334155;
    background: white;
    cursor: pointer;
    transition: all .2s ease;
}

.date-picker-input:hover {
    border-color: #D2691E;
}

.date-picker-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 2px rgba(210, 105, 30, .2);
}

.loader-controls {
    display: flex;
    align-items: center;
    gap: 15px;
}

.toggle-label {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;
}

.toggle-checkbox {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    cursor: pointer;
    accent-color: white;
}

.toggle-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-label {
    display: flex;
    align-items: center;
    gap: 8px;
}

.interval-text {
    color: white;
    font-size: .875rem;
    font-weight: 500;
}

.interval-select {
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(255, 255, 255, .3);
    background: rgba(255, 255, 255, .2);
    color: white;
    font-size: .875rem;
    cursor: pointer;
}

.interval-select option {
    background: #FF8C00;
    color: white;
}

.header-refresh-btn {
    padding: 8px 16px;
    background: rgba(255, 255, 255, .2);
    border: 1px solid rgba(255, 255, 255, .3);
    border-radius: 6px;
    color: white;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: 12px;
}

.header-refresh-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: translateY(-1px);
}

.refresh-icon {
    display: inline-block;
    transition: transform .3s;
}

.header-refresh-btn:hover .refresh-icon {
    transform: rotate(180deg);
}

.live-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, .2);
    padding: 8px 16px;
    border-radius: 20px;
}

.live-dot {
    width: 8px;
    height: 8px;
    background: #4CAF50;
    border-radius: 50%;
    animation: pulse 2s infinite;
}

.live-dot.auto-refresh {
    animation: pulse 1s infinite;
}

@keyframes pulse {
    0%   { box-shadow: 0 0 0 0 rgba(76, 175, 80, .7); }
    70%  { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
    100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
}

.main-content {
    flex: 1;
    overflow: auto;
    padding: 20px;
    width: 100%;
    background: #e2e8f0;
}

.stats-section {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.stats-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.stats-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.toggle-stats-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-stats-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.stats-wrapper {
    padding: 16px 20px;
}

.stats-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}

.stat-card {
    background: #f8fafc;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    transition: all .3s;
    border: 1px solid #e2e8f0;
    position: relative;
    overflow: visible;
}

.stat-card.clickable-stat {
    cursor: pointer;
}

.clickable-stat:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, .15);
    background: #fff;
}

.total-stat     { border-left: 4px solid #FF8C00; }
.volume-stat    { border-left: 4px solid #4CAF50; }
.cost-stat      { border-left: 4px solid #27AE60; }
.equipment-stat { border-left: 4px solid #2196F3; }
.product-stat   { border-left: 4px solid #9C27B0; }
.average-stat   { border-left: 4px solid #E67E22; }

.stat-label {
    color: #2c3e50;
    font-size: .875rem;
    margin-bottom: 8px;
    font-weight: 600;
}

.stat-value {
    font-size: 1.75rem;
    color: #2c3e50;
    font-weight: bold;
}

.stat-change {
    font-size: .75rem;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: #94a3b8;
}

.stat-change.positive { color: #27AE60; }
.stat-change.negative { color: #E74C3C; }

.stat-hint {
    position: absolute;
    bottom: 8px;
    right: 8px;
    font-size: .625rem;
    color: #94a3b8;
    opacity: 0;
    transition: opacity .3s;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: .5px;
}

.clickable-stat:hover .stat-hint { opacity: 1; }

.search-section {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    border: 1px solid #cbd5e1;
}

.search-section.collapsed .filter-header {
    border-radius: 12px;
    border-bottom: none;
}

.filter-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    border-radius: 12px 12px 0 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.filter-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #34495E;
    margin: 0;
}

.filter-header-buttons {
    display: flex;
    gap: 10px;
    align-items: center;
}

.active-filter-badge {
    padding: 4px 10px;
    background: #FF8C00;
    color: white;
    border-radius: 20px;
    font-size: .72rem;
    font-weight: 700;
}

.toggle-filter-btn {
    padding: 8px 16px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: .875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all .3s;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.toggle-filter-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    transform: translateY(-1px);
}

.search-controls {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    padding: 16px 20px;
    align-items: end;
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.filter-group-narrow {
    min-width: 110px;
}

.filter-label {
    font-size: .875rem;
    color: #34495E;
    margin-bottom: 2px;
    font-weight: 500;
}

.filter-select {
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    font-size: 14px;
    transition: all .3s;
    background: #fff;
    color: #1e293b;
    cursor: pointer;
}

.filter-select:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .15);
}

.multi-select-container {
    position: relative;
}

.multi-select-btn {
    width: 100%;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #495057;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    transition: all .3s;
    min-height: 44px;
}

.multi-select-btn:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.dropdown-arrow {
    color: #999;
    font-size: .75rem;
}

.multi-select-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 2px solid #D2691E;
    border-radius: 8px;
    margin-top: 4px;
    max-height: 250px;
    overflow-y: auto;
    z-index: 9999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, .15);
}

.dropdown-search {
    padding: 8px;
    border-bottom: 1px solid #f0f0f0;
    background: #f8f9fa;
}

.select-all-btn {
    width: 100%;
    padding: 6px 10px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    cursor: pointer;
    font-size: .8rem;
    color: #475569;
    font-weight: 500;
    transition: background .2s;
}

.select-all-btn:hover {
    background: #fff3e0;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    font-size: .85rem;
    cursor: pointer;
    transition: background .2s;
    color: #334155;
}

.dropdown-item:hover {
    background: #fff3e0;
}

.dropdown-item input[type="checkbox"] {
    margin-right: 4px;
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #D2691E;
}

.date-time-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    font-size: 14px;
    color: #1e293b;
    background: #fff;
    transition: all .3s;
    min-height: 44px;
}

.date-time-input:hover {
    border-color: #D2691E;
    background: #fff8f3;
}

.date-time-input:focus {
    outline: none;
    border-color: #D2691E;
    box-shadow: 0 0 0 3px rgba(210, 105, 30, .15);
}

.filter-actions {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.action-btn-filter {
    padding: 10px 16px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: .875rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all .3s;
    white-space: nowrap;
}

.action-btn-filter:hover {
    transform: translateY(-1px);
}

.load-btn {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
}

.load-btn:hover {
    background: linear-gradient(135deg, #FF8C00 0%, #FFA500 100%);
    box-shadow: 0 4px 8px rgba(210, 105, 30, .3);
}

.reset-btn {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
}

.reset-btn:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 4px 12px rgba(239, 68, 68, .3);
}

.excel-btn {
    background: white;
    color: #10B981;
    border: 1px solid #D1FAE5;
}

.excel-btn:hover {
    background: #ECFDF5;
    border-color: #10B981;
}

.print-btn {
    background: white;
    color: #8B5CF6;
    border: 1px solid #DDD6FE;
}

.print-btn:hover {
    background: #F3E8FF;
    border-color: #8B5CF6;
}

.grid-container {
    background: #fff;
    border-radius: 12px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, .15);
    overflow: hidden;
    border: 1px solid #cbd5e1;
}

.grid-header {
    padding: 16px 20px;
    background: #f1f5f9;
    border-bottom: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.grid-title {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.report-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #334155;
}

.date-range-label {
    font-size: .9rem;
    color: #64748b;
    font-weight: 500;
    font-style: italic;
}

.grid-controls {
    display: flex;
    align-items: center;
    gap: 20px;
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.grid-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 8px 16px;
    background: white;
    border: 1px solid #cbd5e1;
    border-radius: 6px;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    transition: all .3s;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;
    white-space: nowrap;
}

.action-button:hover {
    background: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, .1);
}

.export-btn {
    color: #10B981;
    border-color: #D1FAE5;
}

.export-btn:hover {
    background: #ECFDF5;
    border-color: #10B981;
}

.popout-btn {
    color: #8B5CF6;
    border-color: #DDD6FE;
}

.popout-btn:hover {
    background: #F3E8FF;
    border-color: #8B5CF6;
}

.loading-indicator {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: .875rem;
    color: #64748b;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #FFE4B5;
    border-top: 4px solid #D2691E;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.large-spinner {
    width: 48px;
    height: 48px;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(241, 245, 249, .95);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

@keyframes spin {
    0% { transform: rotate(0deg) }
    100% { transform: rotate(360deg) }
}

.rows-selector {
    display: flex;
    align-items: center;
    gap: 8px;
}

.rows-label {
    font-size: .875rem;
    color: #6c757d;
    font-weight: 500;
}

.rows-select {
    padding: 6px 12px;
    border: 1px solid #94a3b8;
    border-radius: 6px;
    background: white;
    color: #475569;
    font-size: .875rem;
    cursor: pointer;
    min-width: 80px;
}

.table-container {
    overflow: auto;
    position: relative;
    max-height: 600px;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
}

.data-table thead {
    position: sticky;
    top: 0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    z-index: 10;
}

.data-table th {
    padding: 12px 16px;
    text-align: left;
    color: white;
    font-weight: 600;
    font-size: .875rem;
    text-transform: uppercase;
    letter-spacing: .5px;
    white-space: nowrap;
}

.search-row {
    background: #fff3e0 !important;
}

.search-row th {
    background: transparent !important;
    padding: 6px 8px !important;
    border-right: 1px solid #ffcc80 !important;
}

.col-search {
    width: 100%;
    padding: 5px 8px;
    border: 1px solid #ffcc80;
    border-radius: 4px;
    font-size: .75rem;
    background: white;
    color: #333;
    transition: all .2s;
    font-weight: normal;
    letter-spacing: normal;
    text-transform: none;
    outline: none;
}

.col-search:focus {
    border-color: #ff9800;
    box-shadow: 0 0 0 2px rgba(255, 152, 0, .2);
}

.col-search::placeholder {
    color: #999;
    font-style: italic;
}

.data-table tbody tr {
    border-bottom: 1px solid #e2e8f0;
    transition: all .2s;
    background: #fff;
    cursor: pointer;
}

.data-table tbody tr:hover { background: #f1f5f9; }

.data-row:hover  { background: #fff8f3 !important; }
.row-moving      { background: rgba(39, 174, 96, .04); }
.row-moving:hover { background: rgba(39, 174, 96, .1) !important; }
.row-idle        { background: rgba(255, 193, 7, .05); }
.row-idle:hover  { background: rgba(255, 193, 7, .1) !important; }
.row-failed      { background: rgba(231, 76, 60, .04); border-left: 3px solid #E74C3C; }
.row-failed:hover { background: rgba(231, 76, 60, .08) !important; }

.data-table td {
    padding: 12px 16px;
    font-size: .875rem;
    color: #334155;
}

.id-code {
    font-family: 'Courier New', monospace;
    font-size: .75rem;
    color: #64748b;
    background: #f0f9ff;
    padding: 2px 6px;
    border-radius: 4px;
}

.desc-cell {
    max-width: 180px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.latlng-cell {
    font-family: monospace;
    font-size: .78rem;
    color: #64748b;
}

.priority-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .3px;
}

.pri-high   { background: rgba(231, 76, 60, .15);  color: #b91c1c; }
.pri-medium { background: rgba(255, 193, 7, .2);   color: #a16207; }
.pri-low    { background: #f1f5f9;                 color: #475569; }

.ignition-badge,
.power-badge,
.override-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: .75rem;
    font-weight: 700;
}

.ign-on { background: rgba(39, 174, 96, .15);  color: #065f46; }
.ign-off { background: rgba(231, 76, 60, .12); color: #991b1b; }
.pw-on  { background: rgba(33, 150, 243, .12); color: #0369a1; }
.pw-off { background: #f1f5f9;                 color: #64748b; }
.ov-on  { background: rgba(156, 39, 176, .12); color: #9d174d; }
.ov-off { background: #f1f5f9;                 color: #64748b; }

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: #334155;
    background: #f8fafc;
    border-radius: 8px;
    margin: 20px;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px 20px;
    color: #64748b;
    font-size: .875rem;
}

.empty-icon {
    font-size: 4rem;
    color: #FFE4B5;
    margin-bottom: 16px;
}

.empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 8px;
}

.empty-description {
    color: #6c757d;
    text-align: center;
}

.pagination-container {
    padding: 16px 20px;
    border-top: 1px solid #cbd5e1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #f1f5f9;
}

.pagination-info {
    font-size: .875rem;
    color: #64748b;
}

.pagination-controls {
    display: flex;
    gap: 4px;
}

.page-btn {
    padding: 6px 12px;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #495057;
    font-size: .875rem;
    cursor: pointer;
    transition: all .2s;
    font-weight: 500;
    min-width: 40px;
}

.page-btn:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #D2691E;
    color: #D2691E;
}

.page-btn:disabled {
    opacity: .5;
    cursor: not-allowed;
}

.page-btn.active {
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-color: #D2691E;
}

.simple-popup {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, .5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
}

.popup-content {
    background: white;
    border-radius: 8px;
    width: 90%;
    max-width: 700px;
    max-height: 80vh;
    overflow: auto;
    box-shadow: 0 4px 20px rgba(0, 0, 0, .3);
}

.wide-popup {
    max-width: 900px;
}

.popup-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e0e0e0;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-radius: 8px 8px 0 0;
}

.popup-header h3 {
    margin: 0;
    font-size: 1.25rem;
}

.close-btn {
    background: rgba(255, 255, 255, .2);
    border: none;
    color: white;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all .2s;
    line-height: 1;
}

.close-btn:hover {
    background: rgba(255, 255, 255, .3);
    transform: scale(1.1);
}

.popup-body {
    padding: 20px;
}

.detail-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.detail-section {
    background: #f8fafc;
    border-radius: 8px;
    padding: 16px;
    border: 1px solid #e2e8f0;
}

.detail-section h4 {
    margin: 0 0 12px 0;
    color: #334155;
    font-size: 1rem;
    display: flex;
    align-items: center;
    gap: 8px;
}

.source-icon {
    font-size: 1.25rem;
}

.detail-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.detail-row {
    font-size: .9rem;
}

.detail-row strong {
    color: #34495E;
    display: inline-block;
    min-width: 100px;
}

.slide-fade-enter-active,
.slide-fade-leave-active { transition: all .3s ease; }
.slide-fade-enter,
.slide-fade-leave-to     { transform: translateY(-10px); opacity: 0; }

@media (max-width: 1200px) {
    .stats-container { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 768px) {
    .main-content    { padding: 12px; }
    .stats-container { grid-template-columns: 1fr 1fr; }
    .search-controls { grid-template-columns: 1fr; }
    .data-table      { font-size: .75rem; }
    .data-table th,
    .data-table td   { padding: 8px; }
    .stats-popup     { width: 95vw; max-height: 70vh; }
    .stats-data-table th,
    .stats-data-table td { padding: 8px; font-size: .7rem; }
}

.map-popup {
    width: 980px;
    max-width: 96vw;
}

.map-body {
    display: flex;
    height: 500px;
    overflow: hidden;
}

.map-frame-wrap {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.map-iframe {
    flex: 1;
    width: 100%;
    border: none;
    display: block;
}

.map-no-coords {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748b;
    font-size: .95rem;
    background: #f8fafc;
}

.map-footer {
    padding: 10px 16px;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-shrink: 0;
}

.map-link-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 14px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border-radius: 6px;
    text-decoration: none;
    font-size: .82rem;
    font-weight: 600;
    transition: opacity .2s;
}

.map-link-btn:hover { opacity: .85; }

.map-coords-label {
    font-size: .78rem;
    color: #94a3b8;
    font-family: monospace;
}

.map-info-panel {
    width: 240px;
    flex-shrink: 0;
    background: #f8fafc;
    border-left: 1px solid #e2e8f0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0;
}

.mip-section {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
}

.equipment-section {
    border-left: 3px solid #3498DB;
}

.cost-section {
    border-left: 3px solid #FF9800;
}

.personnel-section {
    border-left: 3px solid #9C27B0;
}

.mip-title {
    font-size: .72rem;
    font-weight: 700;
    color: #D2691E;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 8px;
}

.mip-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 6px;
    margin-bottom: 5px;
    flex-wrap: wrap;
}

.mip-l {
    font-size: .7rem;
    color: #94a3b8;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
}

.mip-v {
    font-size: .75rem;
    color: #1e293b;
    font-weight: 500;
    text-align: right;
    word-break: break-word;
}

.mip-code {
    font-size: .68rem;
    color: #2563eb;
    background: #eff6ff;
    padding: 1px 5px;
    border-radius: 3px;
    font-family: monospace;
    word-break: break-all;
}

.mip-coords {
    font-size: .68rem;
    color: #7c3aed;
    background: #f5f3ff;
    padding: 1px 5px;
    border-radius: 3px;
    font-family: monospace;
}

.map-cell { text-align: center; white-space: nowrap; }

.map-pin-btn {
    padding: 3px 9px;
    background: linear-gradient(135deg, #D2691E 0%, #FF8C00 100%);
    color: white;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: .75rem;
    font-weight: 600;
    transition: opacity .2s, transform .15s;
    white-space: nowrap;
}

.map-btn-wrap {
    position: relative;
    display: inline-block;
}
</style>